import { createAsyncThunk, createSlice, nanoid } from "@reduxjs/toolkit";
import { isEmpty, omit, some, uniq } from "lodash-es";
import { updateUnitApi, loadUnitApi } from "../../apis/buildingUnit";

export const loadUnit = createAsyncThunk(
  "buildingUnitEdit/loadUnit",
  async (buildingUnitId, thunkAPI) => {
    try {
      const response = await loadUnitApi(buildingUnitId);
      return response.data;
    } catch (error) {
      if (error?.response?.data) {
        return thunkAPI.rejectWithValue(error.response.data);
      } else {
        throw error;
      }
    }
  }
);

export const updateUnit = createAsyncThunk(
  "buildingUnitEdit/updateUnit",
  async (data, thunkAPI) => {
    try {
      const response = await updateUnitApi(
        data.buildingUnitId,
        data.payload
      );
      return response.data;
    } catch (error) {
      if (error?.response?.data) {
        return thunkAPI.rejectWithValue(error.response.data);
      } else {
        throw error;
      }
    }
  }
);

const initialState = {
  buildingUnit: {},
  buildingResidents: [],
  currentOwner: {},
  currentRenter: {},
  isOwnerFormShown: false,
  isRenterFormShown: false,
  ownerFormMode: null,
  renterFormMode: null,
  isSubmiting: false,
  isBuildingUnitUpdatedModalShown: false,
  errorMessages: [],
  isBuildingUnitRemoveResidentConfirmationModalShown: false,
  currentResidentAttemptToRemove: {}
};

const buildingUnitEditSlice = createSlice({
  name: "buildingUnitEdit",
  initialState,
  reducers: {
    resetState: (_state, { _payload }) => {
      return initialState;
    },
    addResident: (state, { payload }) => {
      state.buildingResidents = [...state.buildingResidents, payload];
    },
    setCurrentResident: (state, { payload }) => {
      const resident = state.buildingResidents.find(
        (buildingResident) => (buildingResident?.f_id && buildingResident.f_id === payload.f_id) || (buildingResident?.id && buildingResident.id === payload.id)
      );
      if (resident.resident_role === "owner") {
        state.currentOwner = resident;
      } else if (resident.resident_role === "renter") {
        state.currentRenter = resident;
      }
    },
    resetCurrentResident: (state, { payload }) => {
      if (isEmpty(payload)) return;

      if (payload === "owner") state.currentOwner = {};

      if (payload === "renter") state.currentRenter = {};
    },
    setIsOwnerFormShown: (state, { payload }) => {
      state.isOwnerFormShown = payload;
    },
    setIsRenterFormShown: (state, { payload }) => {
      state.isRenterFormShown = payload;
    },
    setOwnerFormMode: (state, { payload }) => {
      state.ownerFormMode = payload;
    },
    setRenterFormMode: (state, { payload }) => {
      state.renterFormMode = payload;
    },
    updateResident: (state, { payload }) => {
      const residentIndex = state.buildingResidents.findIndex(
        (buildingResident) => (buildingResident?.f_id && buildingResident.f_id === payload.f_id) || (buildingResident?.id && buildingResident.id === payload.id)
      );
      state.buildingResidents[residentIndex] = payload;
    },
    setIsBuildingUnitRemoveResidentConfirmationModalShown: (state, { payload }) => {
      state.isBuildingUnitRemoveResidentConfirmationModalShown = payload;
    },
    setCurrentResidentAttemptToRemove: (state, { payload }) => {
      state.currentResidentAttemptToRemove = payload;
    },
    removeResident: (state, { _payload }) => {
      state.buildingResidents = state.buildingResidents.filter(resident => (resident?.f_id && resident.f_id !== state.currentResidentAttemptToRemove.f_id) || (resident?.id && resident.id !== state.currentResidentAttemptToRemove.id));
    },
    setIsBuildingUnitUpdatedModalShown: (state, { payload }) => {
      state.isBuildingUnitUpdatedModalShown = payload;
    },
    resetErrorMessage: (state, { payload }) => {
      state.errorMessages = [];
    }
  },
  extraReducers: (builder) => {
    builder.addCase(loadUnit.fulfilled, (state, { payload }) => {
      state.buildingUnit = omit(payload?.building_unit, 'building_residents');
      state.buildingResidents = payload?.building_unit?.building_residents.map(building_resident => {
        let resident_role;
         if(some(building_resident.roles, ['name', 'owner'])) {
          resident_role = 'owner';
         } else if (some(building_resident.roles, ['name', 'renter'])) {
          resident_role = 'renter';
         }
        return {...building_resident, f_id: nanoid(), resident_role: resident_role };
      });
    }),
    builder.addCase(updateUnit.pending, (state, { payload }) => {
      state.isSubmiting = true;
    }),
    builder.addCase(updateUnit.fulfilled, (state, { payload }) => {
      state.isSubmiting = false;
      state.isBuildingUnitUpdatedModalShown = true;
    }),
    builder.addCase(updateUnit.rejected, (state, { payload }) => {
      if (payload?.error) {
        state.errorMessages = uniq(payload.error);
      } else {
        state.errorMessages = ["Some errors happened, please try again!"];
      }
      state.isSubmiting = false;
      state.isBuildingUnitUpdatedModalShown = false;
    })
  },
});

export const buildingUnitEditPageState = (state) => state.buildingUnitEditPage;

export const {
  resetState,
  addResident,
  setCurrentResident,
  setIsOwnerFormShown,
  setIsRenterFormShown,
  setOwnerFormMode,
  setRenterFormMode,
  resetCurrentResident,
  updateResident,
  setIsBuildingUnitRemoveResidentConfirmationModalShown,
  setCurrentResidentAttemptToRemove,
  removeResident,
  setIsBuildingUnitUpdatedModalShown,
  resetErrorMessage
} = buildingUnitEditSlice.actions;

export const buildingUnitEditPageReducer = buildingUnitEditSlice.reducer;
